/**
 * buildShareImage — renders a 1200×630 PNG "social card" showing a
 * Sudoku Academy achievement with badge art + stats.
 *
 * 1200×630 is the Twitter/Facebook/Discord OG card standard; the same
 * image works as a Twitter image card, a Discord embed preview, and an
 * OpenGraph thumbnail.
 *
 * Returns a Promise<Blob> of a PNG. Caller can call URL.createObjectURL
 * for download, or use ClipboardItem for clipboard copy.
 */

import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import BadgeArt from '../components/BadgeArt';

const CATEGORY_PALETTE = {
  Progress:    { a: '#34C759', b: '#1A9F3F' },
  Milestones:  { a: '#8B5A3C', b: '#0051B3' },
  Speed:       { a: '#FF9F0A', b: '#CC7A00' },
  Mastery:     { a: '#AF52DE', b: '#7B2CBF' },
  Streaks:     { a: '#FF3B30', b: '#B51F1F' },
};

/** Serialize a React SVG element to a data URL. */
function svgDataUrl(element) {
  const markup = renderToStaticMarkup(element);
  // Ensure the root <svg> has xmlns (required for standalone rendering).
  const withNs = markup.replace(
    '<svg ',
    '<svg xmlns="http://www.w3.org/2000/svg" '
  );
  return `data:image/svg+xml;utf8,${encodeURIComponent(withNs)}`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function buildShareImage({
  achievementId,
  name,
  description,
  category,
  username = 'anonymous',
  subtitle = null,   // optional subtitle line (e.g. "Level 12 · Skyscraper · 4:32")
}) {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const pal = CATEGORY_PALETTE[category] || CATEGORY_PALETTE.Progress;

  // Background — dark gradient
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#1A1A2E');
  grad.addColorStop(1, '#0F0F1E');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle Sudoku grid pattern
  ctx.strokeStyle = 'rgba(255,255,255,0.04)';
  ctx.lineWidth = 1;
  const cell = 70;
  for (let x = 0; x <= W; x += cell) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y <= H; y += cell) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  // Heavier 3x3 box lines
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 2;
  for (let x = 0; x <= W; x += cell * 3) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y <= H; y += cell * 3) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // Category accent bar on left
  ctx.fillStyle = pal.a;
  ctx.fillRect(0, 0, 8, H);

  // Draw the badge SVG on the left
  const badgeUrl = svgDataUrl(
    React.createElement(BadgeArt, {
      achievementId,
      category,
      locked: false,
      size: 360,
    })
  );
  try {
    const badgeImg = await loadImage(badgeUrl);
    ctx.drawImage(badgeImg, 90, 135, 360, 360);
  } catch (e) {
    // If the badge fails to load, fall back to a colored circle.
    ctx.fillStyle = pal.a;
    ctx.beginPath();
    ctx.arc(270, 315, 180, 0, Math.PI * 2);
    ctx.fill();
  }

  // Right-side text block
  const textX = 520;
  ctx.textBaseline = 'top';

  // Brand header
  ctx.fillStyle = pal.a;
  ctx.font = '600 28px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('SUDOKU ACADEMY', textX, 100);

  // Achievement name — large
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '800 72px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  wrapText(ctx, name, textX, 150, W - textX - 80, 76);

  // Description
  ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.font = '400 24px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const descY = 150 + estimateLines(ctx, name, W - textX - 80) * 76 + 20;
  wrapText(ctx, description, textX, descY, W - textX - 80, 34);

  // Subtitle (optional solve stats)
  if (subtitle) {
    ctx.fillStyle = pal.a;
    ctx.font = '500 22px "SF Mono", ui-monospace, monospace';
    ctx.fillText(subtitle, textX, 420);
  }

  // Bottom — username + domain
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = '400 22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText(`@${username}`, textX, 500);

  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '500 20px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('sudokuacademy.org', textX, 545);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

// Utility: wrap text into multiple lines, break on whitespace.
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return;
  const words = text.split(/\s+/);
  let line = '';
  let yy = y;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}

function estimateLines(ctx, text, maxWidth) {
  if (!text) return 0;
  const words = text.split(/\s+/);
  let lines = 1;
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines++;
      line = w;
    } else {
      line = test;
    }
  }
  return lines;
}

/** Trigger a browser download of the image. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
